import * as THREE from 'three';
import { CharacterController } from '../physics/CharacterController.js';

const _fwd = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _q = new THREE.Quaternion();

const HALF_PI = Math.PI / 2 - 0.001;

/**
 * The AI rival: a second body running the exact same CharacterController as
 * the player, with its own portal pair. Sharing the controller is the point.
 * A rival with bespoke movement would be either trivially beatable or
 * obviously cheating, and neither is worth racing.
 */
export class Rival {
  constructor({ world, portals, bus, materials }) {
    this.world = world;
    this.portals = portals;
    this.bus = bus;

    this.controller = new CharacterController(world, {
      // Mid-air steer authority. Playtested at 4.5 ("can't even reach a
      // close platform") and 9 ("squirrelly, fling yourself all over"):
      // 6.5 is the tuned midpoint.
      airWishCap: 6.5,
    });
    this.yaw = 0;
    this.pitch = 0;
    this.enabled = false;
    this.finished = false;
    this.finishTime = null;

    this.group = new THREE.Group();
    this.group.visible = false;
    this._build(materials);

    portals.addPair('rival');
    this.controller.onSubstep = (subDt) => portals.updateTraversal(subDt, 'rival');
    portals.registerBody('rival', {
      getSamplePoints: (out) => {
        out[0].copy(this.controller.capsule.start);
        out[1].copy(this.controller.capsule.end);
        return out;
      },
      onTeleport: (matrix) => this.onTeleport(matrix),
    });

    this.stats = { topSpeed: 0 };
    this._trailTimer = 0;
  }

  _build(materials) {
    const c = this.controller;
    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(c.radius, c.height - c.radius * 2, 6, 16),
      new THREE.MeshStandardMaterial({
        color: 0x121b26,
        emissive: new THREE.Color(0x9dff3c).multiplyScalar(0.45),
        emissiveIntensity: 0.9,
        metalness: 0.2,
        roughness: 0.5,
      }),
    );
    body.position.y = c.height * 0.5;
    body.castShadow = true;
    this.group.add(body);
    this.body = body;

    // A visor so you can read which way it is looking from a distance.
    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.09, 0.06),
      new THREE.MeshBasicMaterial({ color: 0xcfff9a, toneMapped: false }),
    );
    visor.position.set(0, c.eyeHeight, -c.radius);
    this.group.add(visor);
    this.visor = visor;

    const halo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: materials.sprites.glow,
      color: 0x9dff3c,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    halo.scale.setScalar(3.2);
    halo.position.y = c.height * 0.5;
    this.group.add(halo);
    this.halo = halo;

    // Speed streak: a stretched quad behind the body, scaled by velocity.
    const trail = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0x9dff3c, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        toneMapped: false,
      }),
    );
    trail.position.y = c.height * 0.5;
    this.group.add(trail);
    this.trail = trail;
  }

  spawn(position, yaw = 0) {
    this.controller.teleportTo(position.x, position.y, position.z);
    this.yaw = yaw;
    this.pitch = 0;
    this.finished = false;
    this.finishTime = null;
    this.stats.topSpeed = 0;
    this.group.visible = this.enabled;
    this.syncVisual();
  }

  setEnabled(on) {
    this.enabled = on;
    this.group.visible = on;
    this.controller.enabled = on;
    if (!on) this.portals.clear('rival');
  }

  get position() { return this.controller.position; }
  get speed() { return this.controller.speed; }

  eye(out) { return this.controller.getEyePosition(out); }

  forward(out = _fwd) {
    const cp = Math.cos(this.pitch);
    return out.set(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
  }

  /** Aim at a world point. Returns the unit direction from the eye. */
  aimAt(point, out = _fwd) {
    this.eye(_tmp);
    out.subVectors(point, _tmp).normalize();
    this.yaw = Math.atan2(-out.x, -out.z);
    this.pitch = Math.asin(THREE.MathUtils.clamp(out.y, -1, 1));
    return out;
  }

  fire(which, targetPoint) {
    const origin = this.eye(new THREE.Vector3());
    const dir = this.aimAt(targetPoint, new THREE.Vector3());
    return this.portals.tryPlace('rival', which, origin, dir, dir);
  }

  update(dt, input) {
    if (!this.enabled) return;
    this.controller.update(dt, {
      moveX: input.moveX ?? 0,
      moveZ: input.moveZ ?? 0,
      yaw: this.yaw,
      jump: input.jump ?? false,
      jumpPressed: input.jumpPressed ?? false,
      sprint: input.sprint ?? true,
    });
    if (this.speed > this.stats.topSpeed) this.stats.topSpeed = this.speed;
    this.syncVisual(dt);
  }

  syncVisual(dt = 0) {
    const p = this.controller.position;
    this.group.position.set(p.x, p.y, p.z);
    this.group.rotation.y = this.yaw;
    this.visor.rotation.x = -this.pitch;

    const s = this.speed;
    if (dt > 0) {
      const stretch = THREE.MathUtils.clamp(s / 22, 0, 3.5);
      this.trail.scale.set(1, 1 + stretch * 3, 1);
      this.trail.material.opacity = Math.min(0.5, stretch * 0.18);
      // Lay the streak along the direction of travel.
      const v = this.controller.velocity;
      if (v.lengthSq() > 1) {
        _tmp.copy(v).normalize();
        this.trail.position.set(
          -_tmp.x * stretch * 0.9,
          this.controller.height * 0.5 - _tmp.y * stretch * 0.9,
          -_tmp.z * stretch * 0.9,
        );
        _q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), _tmp);
        this.trail.quaternion.copy(_q);
        this.trail.quaternion.premultiply(
          new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -this.yaw));
      }
      this.halo.material.opacity = 0.35 + Math.min(0.4, s / 60);
    }
  }

  onTeleport(matrix) {
    this.controller.applyTransform(matrix);
    _q.setFromRotationMatrix(matrix);
    this.forward(_fwd).applyQuaternion(_q).normalize();
    this.yaw = Math.atan2(-_fwd.x, -_fwd.z);
    this.pitch = THREE.MathUtils.clamp(Math.asin(THREE.MathUtils.clamp(_fwd.y, -1, 1)), -HALF_PI, HALF_PI);
    this.syncVisual();
  }

  dispose() {
    this.portals.unregisterBody('rival');
    this.portals.removePair('rival');
    this.group.traverse((o) => {
      o.geometry?.dispose?.();
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
      else o.material?.dispose?.();
    });
  }
}
